export const BASE_URL = 'https://www.fernau-gmbh.de';

/**
 * Default social/OG image for blog posts without a dedicated cover.
 * MUST match `DEFAULT_BLOG_IMAGE` in `scripts/generate-sitemap.ts` so the
 * sitemap's `<image:image>` and the page's `og:image` stay byte-identical.
 */
export const BLOG_DEFAULT_COVER = `${BASE_URL}/og-image.jpg`;

/**
 * Resolve a blog post's cover to an absolute URL.
 *   - absolute http(s) URL         -> returned as-is
 *   - relative path starting with / -> prefixed with BASE_URL
 *   - relative path without /       -> BASE_URL + '/' + value
 *   - empty / whitespace / undefined -> BLOG_DEFAULT_COVER
 * Kept in one place so BlogPost's og:image, Article JSON-LD image, and the
 * sitemap image:image entry can never drift.
 */
export function resolveBlogCover(coverImage?: string | null): string {
  const raw = coverImage?.trim();
  if (!raw) return BLOG_DEFAULT_COVER;
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  return `${BASE_URL}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

export type Lang = 'de' | 'en';

/** Tiny translator local to this module. Defaults to German when called without lang. */
const tr = <T,>(lang: Lang | undefined, de: T, en: T): T =>
  lang === 'en' ? en : de;

/** Localized site root URL (used as page URL / WebSite id base). */
const siteUrl = (lang: Lang | undefined) =>
  lang === 'en' ? `${BASE_URL}/en` : BASE_URL;

export const COMPANY = {
  name: 'FERNAU Präzisionstechnik GmbH',
  legalName: 'Fernau Präzisionstechnik GmbH',
  foundingDate: '1995',
  description: 'CNC-Zerspanungsbetrieb für hochpräzise Dreh- und Frästeile in Darmstadt. ISO 9001 zertifiziert.',
  email: 'info@fernau-gmbh.de',
  phone: '+49 6150 1849730',
  phoneDisplay: '06150 184973-0',
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
  address: {
    street: 'Merianstraße 5a',
    city: 'Darmstadt',
    zip: '64291',
    country: 'DE',
    region: 'Hessen',
  },
  geo: {
    latitude: 49.8605,
    longitude: 8.6283,
  },
  managing: ['Clara Fernau', 'Theodor Fernau', 'Lars Zimmermann'],
  registrationNumber: 'HRB 7378',
  registrationCourt: 'Amtsgericht Darmstadt',
  vatId: 'DE 812 710 783',
} as const;

/**
 * Verified external profiles (sameAs) — one central place. Feeds Google
 * Knowledge Graph, Bing Entity Cards and AI/LLM entity resolution.
 *
 * Nur real existierende, öffentlich abrufbare URLs eintragen — sonst
 * verwirft Google die Entity-Verknüpfung. Erweitern sobald verfügbar:
 *   - LinkedIn Company Page (https://www.linkedin.com/company/...)
 *   - Google Business Profile (https://maps.google.com/?cid=...)
 *   - IHK Darmstadt Firmenprofil
 *   - Handelsregister / Unternehmensregister-Eintrag
 *   - Wikidata Q-ID
 */
export const SAMEAS_URLS: string[] = [
  'https://der-ki-auditor.de',
];

/** JSON-LD: Organization + LocalBusiness. Pass `lang` so description/credentials match the page language. */
export function getOrganizationJsonLd(lang: Lang = 'de') {
  return {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'LocalBusiness'],
    '@id': `${BASE_URL}/#organization`,
    name: COMPANY.name,
    legalName: COMPANY.legalName,
    url: COMPANY.url,
    logo: COMPANY.logo,
    foundingDate: COMPANY.foundingDate,
    description: tr(
      lang,
      COMPANY.description,
      'CNC machining shop for high-precision turned and milled parts in Darmstadt, Germany. ISO 9001 certified.',
    ),
    email: COMPANY.email,
    telephone: COMPANY.phone,
    vatID: COMPANY.vatId,
    iso6523Code: COMPANY.registrationNumber,
    address: {
      '@type': 'PostalAddress',
      streetAddress: COMPANY.address.street,
      addressLocality: COMPANY.address.city,
      postalCode: COMPANY.address.zip,
      addressRegion: COMPANY.address.region,
      addressCountry: COMPANY.address.country,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: COMPANY.geo.latitude,
      longitude: COMPANY.geo.longitude,
    },
    hasCredential: {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'ISO 9001:2015',
      name: tr(lang, 'DIN EN ISO 9001:2015 Zertifizierung', 'DIN EN ISO 9001:2015 certification'),
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
        opens: '07:30',
        closes: '16:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Friday'],
        opens: '07:30',
        closes: '12:30',
      },
    ],
    sameAs: SAMEAS_URLS,
    numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 10, maxValue: 20 },
    slogan: tr(
      lang,
      'Präzision aus Darmstadt seit 1995 — CNC-Lohnfertigung & KI-Beratung für den Mittelstand.',
      'Precision from Darmstadt since 1995 — CNC contract manufacturing & AI consulting for SMEs.',
    ),
    areaServed: [
      { '@type': 'Country', name: tr(lang, 'Deutschland', 'Germany') },
      { '@type': 'State', name: tr(lang, 'Hessen', 'Hesse') },
      { '@type': 'City', name: 'Darmstadt' },
      { '@type': 'City', name: 'Frankfurt am Main' },
      { '@type': 'City', name: 'Offenbach am Main' },
      { '@type': 'City', name: 'Hanau' },
      { '@type': 'City', name: 'Wiesbaden' },
      { '@type': 'City', name: 'Mainz' },
      { '@type': 'City', name: 'Aschaffenburg' },
      { '@type': 'City', name: 'Heidelberg' },
      { '@type': 'City', name: 'Mannheim' },
      { '@type': 'Place', name: tr(lang, 'Rhein-Main-Gebiet', 'Rhine-Main metropolitan area') },
      {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: COMPANY.geo.latitude,
          longitude: COMPANY.geo.longitude,
        },
        geoRadius: '200000',
      },
    ],
    knowsAbout: tr(
      lang,
      ['CNC-Drehen', 'CNC-Fräsen', 'Baugruppenfertigung', 'CAD/CAM-Konstruktion', 'Präzisionszerspanung', 'ISO 9001'],
      ['CNC turning', 'CNC milling', 'Assembly manufacturing', 'CAD/CAM design', 'Precision machining', 'ISO 9001'],
    ),
    inLanguage: lang,
  };
}

/** JSON-LD: WebSite with SearchAction for AI/GEO discovery. */
export function getWebSiteJsonLd(lang: Lang = 'de') {
  const root = siteUrl(lang);
  const servicesPath = lang === 'en' ? '/en/leistungen' : '/leistungen';
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    name: COMPANY.name,
    url: root,
    publisher: { '@id': `${BASE_URL}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${BASE_URL}${servicesPath}?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
    inLanguage: lang,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '.hero-description', '.faq-answer'],
    },
  };
}

/** JSON-LD: BreadcrumbList helper. Pass lang='en' for English crumbs (uses /en prefix). */
export function getBreadcrumbJsonLd(
  items: { name: string; path: string }[],
  lang: Lang = 'de',
) {
  const prefix = lang === 'en' ? '/en' : '';
  const homeName = lang === 'en' ? 'Home' : 'Startseite';
  const homeUrl = lang === 'en' ? `${BASE_URL}/en` : BASE_URL;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: homeName, item: homeUrl },
      ...items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: item.name,
        item: `${BASE_URL}${prefix}${item.path}`,
      })),
    ],
  };
}

/** JSON-LD: Service helper */
export function getServiceJsonLd(service: {
  name: string;
  description: string;
  url: string;
}, lang: Lang = 'de') {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    provider: { '@id': `${BASE_URL}/#organization` },
    name: service.name,
    description: service.description,
    url: service.url,
    areaServed: 'DE',
    inLanguage: lang,
  };
}


/** JSON-LD: FAQPage helper */
export function getFaqJsonLd(
  faqs: { question: string; answer: string }[],
  lang: Lang = 'de',
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: lang,
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/** JSON-LD: ProfessionalService for Consulting */
export function getProfessionalServiceJsonLd(lang: Lang = 'de') {
  const url = lang === 'en' ? `${BASE_URL}/en/consulting` : `${BASE_URL}/consulting`;
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${BASE_URL}/consulting/#service`,
    name: tr(lang, 'FERNAU KI-Beratung & Digitalisierung', 'FERNAU AI Consulting & Digital Transformation'),
    url,
    provider: { '@id': `${BASE_URL}/#organization` },
    description: tr(
      lang,
      'KI-Beratung und Digitalisierungsberatung für den Mittelstand. Von KI-Agenten und Predictive Maintenance über Prozessautomatisierung bis zur EU AI Act Compliance. 20 Jahre Industriepraxis aus Darmstadt.',
      'AI consulting and digital transformation for SMEs. From AI agents and predictive maintenance through process automation to EU AI Act compliance. 20 years of industrial practice from Darmstadt, Germany.',
    ),
    areaServed: tr(
      lang,
      [
        { '@type': 'Country', name: 'Deutschland' },
        { '@type': 'State', name: 'Hessen' },
        { '@type': 'Place', name: 'Europa' },
      ],
      [
        { '@type': 'Country', name: 'Germany' },
        { '@type': 'State', name: 'Hesse' },
        { '@type': 'Place', name: 'Europe' },
      ],
    ),
    serviceType: [
      'KI-Beratung',
      'Digitalisierungsberatung',
      'Prozessautomatisierung',
      'KI-Strategie',
      'EU AI Act Compliance',
      'Change Management',
      'Lean Production',
      'Industrie 4.0 Beratung',
      'KI Readiness Assessment',
      'KI Pilotprojekt',
      'NIS2 Compliance',
      'DORA Compliance',
      'KI Schulung & Training',
      'AI consulting for SMEs',
      'AI strategy small business',
      'AI process automation SME',
    ],
    inLanguage: lang,
    knowsAbout: [
      'Künstliche Intelligenz',
      'KI-Agenten',
      'Agentic AI',
      'Predictive Maintenance',
      'Predictive Quality',
      'Prozessautomatisierung',
      'Digitale Transformation',
      'Industrie 4.0',
      'EU AI Act',
      'Change Management',
      'Lean Production',
      'ERP-Integration',
      'Digitaler Zwilling',
      'Low-Code Automatisierung',
      'Datenanalyse Fertigung',
      'KI-gestützte Qualitätskontrolle',
      'Fachkräftemangel Lösungen',
      'KI Förderung Mittelstand',
      'NIS2',
      'DORA',
      'ISO 42001',
      'KI Readiness Check',
      'ChatGPT Integration',
      'Generative AI',
      'Machine Learning KMU',
      'RPA',
      'KI Pilotprojekt',
      'KI Trainer',
      'KI Schulung',
      'Mittelstand Digital',
      'DSGVO konforme KI',
      'AI consulting SME',
      'AI readiness assessment',
      'AI Act compliance SME',
      'AI implementation SMB',
      'AI for manufacturing SME',
      'responsible AI consulting Europe',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: tr(lang, 'Consulting-Leistungen', 'Consulting services'),
      itemListElement: tr(lang, [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'KI-Beratung für KMU',
            description: 'Systematische Identifikation und Umsetzung von KI-Potenzialen im Mittelstand, von der Strategie bis zur produktiven Lösung.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'KI-Agenten & Agentic AI',
            description: 'Entwicklung und Integration autonomer KI-Agenten für automatisierte Geschäftsprozesse, Angebotserstellung und Kundenservice.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Predictive Maintenance & Predictive Quality',
            description: 'Vorausschauende Wartung und KI-gestützte Qualitätskontrolle für produzierende Unternehmen zur Reduktion von Ausfallzeiten.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Prozessautomatisierung & Workflow-Automation',
            description: 'Automatisierung wiederkehrender Geschäftsprozesse mit KI-gestützten Workflows, RPA und intelligenter Dokumentenverarbeitung.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Digitalisierungsberatung Mittelstand',
            description: 'Ganzheitliche Digitalisierungsstrategie für KMU, von der ERP-Integration über Cloud-Migration bis zur datenbasierten Fertigung.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'EU AI Act Compliance & KI-Governance',
            description: 'Risikobewertung, Klassifizierung und Compliance-Beratung gemäß der EU-KI-Verordnung für den rechtskonformen KI-Einsatz.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Change Management & KI-Enablement',
            description: 'Begleitung von Teams durch digitale Transformation, Schulungen und praxisnahe Befähigung für den KI-Einsatz im Arbeitsalltag.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Lean Production & Shopfloor-Optimierung',
            description: 'KI-gestützte Lean-Methoden zur Verschwendungseliminierung, Durchlaufzeitverkürzung und datenbasierter Shopfloor-Steuerung.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'KI Readiness Check & Pilotprojekt',
            description: 'Systematische Bewertung der KI-Reife Ihres Unternehmens mit anschließendem Pilotprojekt. Von der Ist-Analyse über die Tool-Auswahl bis zum produktiven Proof of Concept.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'NIS2 & DORA Compliance-Beratung',
            description: 'Compliance-Beratung für die neuen EU-Regulierungen NIS2 und DORA. Risikobewertung, Gap-Analyse und Umsetzungsbegleitung für KMU im regulierten Umfeld.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'KI-Schulung & Enablement für Teams',
            description: 'Praxisnahe KI-Schulungen und Workshops für Führungskräfte und Mitarbeiter. ChatGPT-Integration, Generative AI im Arbeitsalltag und KI-Kompetenzaufbau im Mittelstand.',
          },
        },
      ], [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI consulting for SMEs', description: 'Systematic identification and delivery of AI potential in mid-sized companies, from strategy to production.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI agents & agentic AI', description: 'Development and integration of autonomous AI agents for automated business processes, quoting and customer service.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Predictive maintenance & predictive quality', description: 'Predictive maintenance and AI-supported quality control for manufacturing companies to reduce downtime.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Process automation & workflow automation', description: 'Automation of recurring business processes with AI workflows, RPA and intelligent document processing.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Digital transformation consulting for SMEs', description: 'End-to-end digital strategy for SMEs, from ERP integration through cloud migration to data-driven manufacturing.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'EU AI Act compliance & AI governance', description: 'Risk assessment, classification and compliance advisory under the EU AI Regulation for compliant AI usage.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Change management & AI enablement', description: 'Guiding teams through digital transformation, training and hands-on enablement for AI in daily work.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Lean production & shop floor optimisation', description: 'AI-supported lean methods for waste reduction, lead-time shortening and data-driven shop floor control.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI readiness check & pilot project', description: 'Systematic assessment of your AI maturity followed by a pilot project, from baseline analysis through tool selection to productive proof of concept.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'NIS2 & DORA compliance advisory', description: 'Compliance advisory for the new EU regulations NIS2 and DORA. Risk assessment, gap analysis and delivery support for regulated SMEs.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI training & enablement for teams', description: 'Hands-on AI training and workshops for executives and employees. ChatGPT integration, generative AI in daily work and AI literacy building in SMEs.' } },
      ]),
    },
  };
}

/** JSON-LD: Person schema for Lars Zimmermann */
export function getConsultantPersonJsonLd(lang: Lang = 'de') {
  const url = lang === 'en' ? `${BASE_URL}/en/consulting` : `${BASE_URL}/consulting`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Lars Zimmermann',
    jobTitle: tr(lang, 'Geschäftsführer & KI-Manager', 'Managing Director & AI Manager'),
    worksFor: { '@id': `${BASE_URL}/#organization` },
    url,
    description: tr(
      lang,
      'Operational Turnaround Architect mit 20 Jahren Industrie- und Führungserfahrung. KI Manager (IHK), spezialisiert auf KI-Strategie, Digitalisierung und Prozessautomatisierung im Mittelstand.',
      'Operational turnaround architect with 20 years of industrial and leadership experience. AI Manager (IHK), specialised in AI strategy, digital transformation and process automation for SMEs.',
    ),
    knowsAbout: [
      'KI-Beratung',
      'Digitalisierung',
      'Prozessautomatisierung',
      'Industrie 4.0',
      'Lean Production',
      'Change Management',
      'EU AI Act',
      'NIS2',
      'DORA',
      'ISO 42001',
      'KI Readiness',
      'Generative AI',
      'ChatGPT Integration',
      'AI consulting SME',
    ],
    hasCredential: [
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: tr(lang, 'Zertifizierung', 'Certification'),
        name: 'KI Manager (IHK)',
      },
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: tr(lang, 'Zertifizierung', 'Certification'),
        name: 'Senior Lead Auditor ISO/IEC 42001',
      },
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: tr(lang, 'Zertifizierung', 'Certification'),
        name: 'Lead Auditor ISO/IEC 27001',
      },
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: tr(lang, 'Weiterbildung', 'Continuing education'),
        name: 'Geprüfter Wirtschaftsfachwirt (IHK)',
      },
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: tr(lang, 'Meisterqualifikation', 'Master craftsman qualification'),
        name: 'Meister Metallbau (HWK)',
      },
    ],
  };
}

/** JSON-LD: Person schema for Lars Zimmermann on the About page */
export function getLarsZimmermannAboutJsonLd(lang: Lang = 'de') {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Lars Zimmermann',
    jobTitle: tr(lang, 'Geschäftsführer', 'Managing Director'),
    worksFor: {
      '@type': 'Organization',
      name: 'FERNAU Präzisionstechnik GmbH',
      url: 'https://fernau-gmbh.de',
    },
    hasCredential: [
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'certification',
        name: 'ISO/IEC 42001 Senior Lead Auditor (PECB)',
      },
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'certification',
        name: 'ISO/IEC 27001 Lead Auditor (PECB)',
      },
    ],
    knowsAbout: tr(
      lang,
      ['ISO/IEC 42001', 'ISO/IEC 27001', 'EU AI Act', 'KI-Managementsysteme', 'KI-Governance', 'Informationssicherheit', 'Second-Party-Audit'],
      ['ISO/IEC 42001', 'ISO/IEC 27001', 'EU AI Act', 'AI management systems', 'AI governance', 'Information security', 'Second-party audit'],
    ),
    sameAs: [
      'https://der-ki-auditor.de',
      'https://www.linkedin.com/in/lars-zimmermann-89601034a/',
      'https://www.credly.com/badges/29aed2cf-c4b8-45ec-98b4-bffdeedfd959/public_url',
      'https://www.credly.com/badges/fb7ada28-8a30-46ec-9782-cf3ecd60d0ad/public_url',
    ],
  };
}

/** JSON-LD: Service helper for SEO landing pages (provider-referenced) */
export function getLandingPageJsonLd(params: {
  name: string;
  description: string;
  path: string;
  serviceType: string[];
  serviceOutput?: string;
  priceRange?: { min: number; max: number; currency?: string };
}, lang: Lang = 'de') {
  const url = `${siteUrl(lang)}${params.path}`;
  const base: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${url}/#service`,
    name: params.name,
    description: params.description,
    url,
    provider: { '@id': `${BASE_URL}/#organization` },
    areaServed: tr(
      lang,
      [
        { '@type': 'Country', name: 'Deutschland' },
        { '@type': 'State', name: 'Hessen' },
      ],
      [
        { '@type': 'Country', name: 'Germany' },
        { '@type': 'State', name: 'Hesse' },
      ],
    ),
    serviceType: params.serviceType,
    inLanguage: lang,
    audience: {
      '@type': 'BusinessAudience',
      audienceType: tr(
        lang,
        'KMU / Mittelstand',
        'Small and medium-sized enterprises (SME)',
      ),
      geographicArea: {
        '@type': 'Country',
        name: tr(lang, 'Deutschland', 'Germany'),
      },
    },
  };
  if (params.serviceOutput) {
    base.serviceOutput = params.serviceOutput;
  }
  if (params.priceRange) {
    base.offers = {
      '@type': 'Offer',
      priceSpecification: {
        '@type': 'PriceSpecification',
        minPrice: params.priceRange.min,
        maxPrice: params.priceRange.max,
        priceCurrency: params.priceRange.currency ?? 'EUR',
      },
      availability: 'https://schema.org/InStock',
      url,
    };
  }
  return base;
}

/** JSON-LD: HowTo helper — strong signal for AI Overviews and rich results */
export function getHowToJsonLd(params: {
  name: string;
  description: string;
  steps: { title: string; body: string }[];
}, lang: Lang = 'de') {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: params.name,
    description: params.description,
    inLanguage: lang,
    step: params.steps.map((step, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: step.title,
      text: step.body,
    })),
  };
}

/** JSON-LD: Article helper — E-E-A-T signal with author and dateModified */
export function getArticleJsonLd(params: {
  title: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified: string;
  /** Optional per-post cover. Falls back to the sitewide og-image. */
  coverImage?: string;
  /** Cited sources, rendered as schema.org citation + isBasedOn entries. */
  sources?: Array<{ title: string; url: string; publisher?: string }>;
  /** Blog category label, mapped to articleSection. */
  section?: string;
  /** Body word count for E-E-A-T depth signals. */
  wordCount?: number;
}, lang: Lang = 'de') {
  const url = `${siteUrl(lang)}${params.path}`;
  const authorUrl = lang === 'en' ? `${BASE_URL}/en/consulting` : `${BASE_URL}/consulting`;
  const coverUrl = resolveBlogCover(params.coverImage);
  const imageObject = {
    '@type': 'ImageObject' as const,
    url: coverUrl,
    headline: params.title,
    caption: params.description,
  };
  const sources = (params.sources ?? []).filter((s) => Boolean(s?.url));
  const citations = sources.map((s) => ({
    '@type': 'CreativeWork' as const,
    '@id': s.url,
    name: s.title,
    url: s.url,
    ...(s.publisher
      ? { publisher: { '@type': 'Organization' as const, name: s.publisher } }
      : {}),
  }));
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${url}#article`,
    headline: params.title,
    description: params.description,
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    datePublished: params.datePublished,
    dateModified: params.dateModified,
    inLanguage: lang,
    ...(params.section ? { articleSection: params.section } : {}),
    ...(params.wordCount ? { wordCount: params.wordCount } : {}),
    author: {
      '@type': 'Person',
      name: 'Lars Zimmermann',
      jobTitle: tr(lang, 'Geschäftsführer & KI-Manager', 'Managing Director & AI Manager'),
      url: authorUrl,
      worksFor: { '@id': `${BASE_URL}/#organization` },
    },
    publisher: { '@id': `${BASE_URL}/#organization` },
    image: imageObject,
    ...(citations.length ? { citation: citations, isBasedOn: citations.map((c) => c['@id']) } : {}),
  };
}

/** Standard hreflang set for DE landing pages */
export function getStandardHreflang(path: string) {
  const href = `${BASE_URL}${path}`;
  return [
    { lang: 'de', href },
    { lang: 'de-DE', href },
    { lang: 'x-default', href },
  ];
}

/** JSON-LD: Sister brand DER-KI-AUDITOR — separate Organization, referenced from Consulting */
export function getKiAuditorBrandJsonLd(lang: Lang = 'de') {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://www.der-ki-auditor.de/#organization',
    name: 'DER-KI-AUDITOR',
    alternateName: 'Der KI-Auditor',
    url: 'https://www.der-ki-auditor.de',
    description: tr(
      lang,
      'Unabhängige KI-Audits und Informationssicherheits-Audits nach ISO/IEC 42001 und ISO/IEC 27001 für den deutschen Mittelstand. Schwestermarke der FERNAU Präzisionstechnik GmbH.',
      'Independent AI audits and information security audits to ISO/IEC 42001 and ISO/IEC 27001 for German SMEs. Sister brand of FERNAU Präzisionstechnik GmbH.',
    ),
    parentOrganization: { '@id': `${BASE_URL}/#organization` },
    founder: {
      '@type': 'Person',
      name: 'Lars Zimmermann',
    },
    employee: {
      '@type': 'Person',
      name: 'Lars Zimmermann',
    },
    areaServed: tr(
      lang,
      [
        { '@type': 'Country', name: 'Deutschland' },
        { '@type': 'Place', name: 'DACH' },
      ],
      [
        { '@type': 'Country', name: 'Germany' },
        { '@type': 'Place', name: 'DACH' },
      ],
    ),
    knowsAbout: [
      'ISO/IEC 42001',
      'ISO/IEC 27001',
      'EU AI Act',
      'KI-Audit',
      'KI-Governance',
      'KI-Managementsystem',
      'Informationssicherheit',
      'AI Management System Audit',
      'AIMS',
      'ISMS',
    ],
    inLanguage: lang,
    hasCredential: [
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: tr(lang, 'PECB Zertifizierung', 'PECB certification'),
        name: 'ISO/IEC 42001 Senior Lead Auditor (PECB)',
        url: 'https://www.credly.com/badges/29aed2cf-c4b8-45ec-98b4-bffdeedfd959/public_url',
      },
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: tr(lang, 'PECB Zertifizierung', 'PECB certification'),
        name: 'ISO/IEC 27001 Lead Auditor (PECB)',
        url: 'https://www.credly.com/badges/fb7ada28-8a30-46ec-9782-cf3ecd60d0ad/public_url',
      },
    ],
    sameAs: [
      'https://www.der-ki-auditor.de',
      'https://www.credly.com/badges/29aed2cf-c4b8-45ec-98b4-bffdeedfd959/public_url',
      'https://www.credly.com/badges/fb7ada28-8a30-46ec-9782-cf3ecd60d0ad/public_url',
    ],
  };
}
